import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useBusinesses } from "@/hooks/use-businesses";
import { SubscriptionRequired } from "@/components/subscription-required";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Search, Scale, BookOpen, BookText, AlertCircle, Trash2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";

export default function TaxResearchPage() {
  const { toast } = useToast();
  const { activeBusiness } = useBusinesses();
  const [query, setQuery] = useState("");
  const [searchHistory, setSearchHistory] = useState<{
    query: string;
    answer: string;
    sources?: string[];
    disclaimer: string;
    timestamp: Date;
  }[]>([]);

  // Tax Research mutation
  const taxResearchMutation = useMutation({
    mutationFn: async ({ query }: { query: string }) => {
      const res = await apiRequest("POST", "/api/tax-research", {
        query,
      });
      return res.json();
    },
    onSuccess: (data) => {
      // Add search to history
      const searchResult = {
        query,
        answer: data.answer,
        sources: data.sources,
        disclaimer: data.disclaimer,
        timestamp: new Date(),
      };
      setSearchHistory([searchResult, ...searchHistory]);
      
      // Reset query field
      setQuery("");
      
      // Show success toast
      toast({
        title: "Tax research complete",
        description: "Your tax question has been answered",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Tax research failed",
        description: error.message || "Failed to get tax information",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!activeBusiness) {
      toast({
        title: "No business selected",
        description: "Please select a business first",
        variant: "destructive",
      });
      return;
    }
    
    if (!query.trim()) {
      toast({
        title: "Query required",
        description: "Please enter a tax question",
        variant: "destructive",
      });
      return;
    }
    
    taxResearchMutation.mutate({
      query: query.trim(),
    });
  };

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tax Research</h1>
          <p className="text-muted-foreground">
            Get reliable answers to your tax questions powered by AI
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSearchHistory([])}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Clear History
          </Button>
          <Scale className="h-10 w-10 text-primary" />
        </div>
      </div>
      
      <Separator />
      
      {activeBusiness ? (
        <div className="grid grid-cols-1 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookText className="h-5 w-5 text-primary" />
                  Ask a Tax Question
                </CardTitle>
                <CardDescription>
                  Enter your tax question to get reliable answers with references to relevant tax regulations
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleSubmit}>
                <CardContent>
                  <div className="space-y-4">
                    <Textarea
                      placeholder="E.g., What are the tax implications of hiring contractors vs employees?"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="min-h-[120px] resize-none"
                    />
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        This tool provides general information only. Always consult with a tax professional for advice specific to your situation.
                      </AlertDescription>
                    </Alert>
                  </div>
                </CardContent>
                <CardFooter className="justify-between items-center">
                  <p className="text-sm text-muted-foreground">
                    Business context from <span className="font-medium">{activeBusiness.name}</span> will be used to improve answers
                  </p>
                  <Button 
                    type="submit" 
                    disabled={taxResearchMutation.isPending || !query.trim()}
                  >
                    {taxResearchMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Researching...
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-4 w-4" />
                        Research
                      </>
                    )}
                  </Button>
                </CardFooter>
              </form>
            </Card>
            
            {searchHistory.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-primary" />
                    Research Results
                  </CardTitle>
                  <CardDescription>
                    Your recent tax research results
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px] pr-4">
                    <div className="space-y-8">
                      {searchHistory.map((search, index) => (
                        <div key={index} className="border rounded-lg p-4 space-y-4">
                          <div>
                            <h3 className="font-semibold text-lg">Question:</h3>
                            <p className="mt-1">{search.query}</p>
                          </div>
                          
                          <div>
                            <h3 className="font-semibold text-lg">Answer:</h3>
                            <div className="mt-1 whitespace-pre-line">
                              {search.answer.split('\n').map((line, i) => (
                                <p key={i} className="mb-2">{line}</p>
                              ))}
                            </div>
                          </div>
                          
                          {search.sources && search.sources.length > 0 && (
                            <div>
                              <h3 className="font-semibold text-lg">Sources:</h3>
                              <ul className="list-disc pl-5 mt-1">
                                {search.sources.map((source, i) => (
                                  <li key={i}>{source}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          <div className="bg-muted p-3 rounded-md text-xs text-muted-foreground">
                            <p>{search.disclaimer}</p>
                          </div>
                          
                          <div className="text-xs text-muted-foreground pt-2 border-t">
                            Researched on {search.timestamp.toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>No Business Selected</CardTitle>
            <CardDescription>
              Please select a business to use the Tax Research tool
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Select a business from the business switcher in the sidebar to get started
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}
      </div>
    </Layout>
  );
}